import { Button } from '@/components/ui/button.tsx';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import { createContext, useContext } from 'react';
import type { BackgroundTask, BackgroundTaskStatus } from '@ozon-ai/ozon/contracts';
import { cn } from 'cn';

const STATUS_LABEL: Record<BackgroundTaskStatus, string> = {
  queued: 'В очереди',
  running: 'Выполняется',
  succeeded: 'Готово',
  failed: 'Ошибка',
};

const BackgroundTasksPanelContext = createContext({
  openPanel: () => undefined as void,
});

export function useBackgroundTasksPanel() {
  return useContext(BackgroundTasksPanelContext);
}

export const backgroundTasksPanelContext = BackgroundTasksPanelContext;

export function BackgroundTasksPanel({
  tasks,
  onClose,
}: {
  tasks: BackgroundTask[];
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30">
      <button
        type="button"
        className="absolute inset-0 bg-black/35"
        aria-label="Закрыть панель задач"
        onClick={onClose}
      />
      <div
        className={cn(
          'absolute top-0 right-0 bottom-0 flex w-[360px] max-w-[92%] flex-col gap-3',
          'border-l border-border bg-background p-3',
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="text-lg font-semibold">Активные задачи</p>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Закрыть
          </Button>
        </div>
        <ScrollArea className="min-h-0 flex-1">
          <div className="flex flex-col gap-3 pr-2">
            {tasks.length === 0 && (
              <p className="text-xs text-muted-foreground">Нет задач</p>
            )}
            {tasks.map((task) => (
              <TaskCard key={task.id} task={task} />
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

function TaskCard({ task }: { task: BackgroundTask }) {
  const started = task.startedAt ?? task.createdAt;
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border p-2">
      <p className="text-sm font-semibold">{task.title}</p>
      <p
        className={cn(
          'text-xs',
          task.status === 'failed' ? 'text-destructive' : 'text-foreground',
        )}
      >
        {STATUS_LABEL[task.status]}
      </p>
      {task.message ? (
        <p className="text-xs text-muted-foreground">
          {task.message}
          {task.total > 0 ? ` — ${task.current} из ${task.total}` : ''}
        </p>
      ) : null}
      <p className="text-[11px] text-muted-foreground">Старт: {formatStarted(started)}</p>
      {task.error && task.status === 'failed' && task.error !== task.message ? (
        <p className="text-xs text-destructive">{task.error}</p>
      ) : null}
    </div>
  );
}

function formatStarted(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
